import { defineStore } from 'pinia'
import type { Tenant, Base } from '@/types'

interface TenantState {
  tenant: Tenant | null
  base: Base | null
}

export const useTenantStore = defineStore('tenant', {
  state: (): TenantState => ({
    tenant: null,
    base: null
  }),

  getters: {
    // Loading state - simply check if tenant exists
    isLoaded: (state): boolean => state.tenant !== null,

    // Plan checking
    plan: (state): string | null => state.tenant?.plan || null,
    isPro: (state): boolean => state.tenant?.plan === 'pro',

    // Tenant details
    tenantKey: (state): string | null => state.tenant?.lark_tenant_key || null,
    tenantName: (state): string | null => state.tenant?.name || null,
    monthlyQuota: (state): number | null => state.tenant?.monthly_quota || null,
    currentQuota: (state): number | null => state.tenant?.current_quota || null,
    quotaResetDay: (state): number | null => state.tenant?.quota_reset_day_number || null,

    // Quota usage (computed)
    quotaUsed: (state): number | null => {
      if (!state.tenant?.monthly_quota) return null
      const used = state.tenant.monthly_quota - (state.tenant.current_quota || 0)
      return Math.max(0, used)
    },
    quotaPercentage: (state): number | null => {
      if (!state.tenant?.monthly_quota) return null
      const used = state.tenant.monthly_quota - (state.tenant.current_quota || 0)
      return Math.round((used / state.tenant.monthly_quota) * 100)
    },

    // Base token checking
    hasPersonalBaseToken: (state): boolean => {
      return !!state.base?.personal_base_token
    },

    // Unlimited quota (null means unlimited)
    hasUnlimitedQuota: (state): boolean => {
      return state.tenant?.monthly_quota === null
    }
  },

  actions: {
    setTenant(tenantData: Tenant): void {
      this.tenant = tenantData
    },

    setBase(baseData: Base | null): void {
      this.base = baseData
    },

    setTenantAndBase(tenantData: Tenant, baseData: Base | null): void {
      this.tenant = tenantData
      this.base = baseData
    },

    updateTenantName(name: string): void {
      if (this.tenant) {
        this.tenant.name = name
      }
    },

    reset(): void {
      this.tenant = null
      this.base = null
    }
  }
})
